import { useMemo, useState } from 'react';

import { Editor } from '@tiptap/react';
import { useFormik } from 'formik';

import {
  adminSendCampaignMail,
  getCampaignTemplates,
  HelpTypes,
  Language,
  setCampaignEmailTemplates,
} from '@pharmaplan/common';

import testEmailValidation from '../../../components/Admin/Campaigns/ConfigureCampaignEmail/validation';
import { formKeys } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';

import useTransformCampaignTemplates from './useTransformCampaignTemplates';

export const emailObjectTypes = {
  template: 'Template',
  image: 'Image',
};

export const emailProps = {
  subject: 'subject',
  body: 'body',
};

const sharedEmailImageFields: Partial<Record<formKeys, formKeys>> = {
  [formKeys.existingUserEmail]: formKeys.registrationEmail,
  [formKeys.multiBookingEmail]: formKeys.bookingEmail,
};

const useConfigureCampaignEmail = (
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>,
  userType: HelpTypes,
) => {
  const dispatch = useAppDispatch();
  const { imageTemplateConfig } = useTransformCampaignTemplates();

  const [language, setLanguage] = useState(Language.fr);
  const [editor, setEditor] = useState<Editor | null>(null);
  const emailTemplateInitial = useAppSelector(getCampaignTemplates);

  const onEditorReady = (_editor: Editor | null) => {
    setEditor(_editor);
  };

  const formik = useFormik({
    initialValues: emailTemplateInitial,
    enableReinitialize: true,
    onSubmit: (values) => {
      dispatch(setCampaignEmailTemplates(values));
      setCurrentStep((prev) =>
        prev - 1);
      console.log(editor?.getHTML());
    },
  });

  const isSendDisabled = !(
    formik.values[
      `${formik.values?.emailTemplateItem}${emailObjectTypes.template}${emailProps.body}${Language.fr}` as keyof typeof formik.values
    ]
    && formik.values[
      `${formik.values?.emailTemplateItem}${emailObjectTypes.template}${emailProps.subject}${Language.fr}` as keyof typeof formik.values
    ]
  );

  const getActiveFieldNames = useMemo(
    () => {
      const selectedTemplate = formik.values.emailTemplateItem as formKeys;
      const imageTemplate = sharedEmailImageFields[selectedTemplate]
        ?? selectedTemplate;

      return {
        imageName: `${imageTemplate}${emailObjectTypes.image}`,
        bodyName: `${formik.values?.emailTemplateItem}${emailObjectTypes.template}${emailProps.body}${language}`,
        subjectName: `${formik.values?.emailTemplateItem}${emailObjectTypes.template}${emailProps.subject}${language}`,
      };
    },
    [language, formik.values.emailTemplateItem],
  );

  const getFormikValue = (fieldName: string) =>
    `${formik.values[fieldName as keyof typeof formik.values] ?? ''}`.trim();

  const activeEmailBody = getFormikValue(getActiveFieldNames.bodyName);
  const activeEmailSubject = getFormikValue(getActiveFieldNames.subjectName);
  const activeEmailImage = getFormikValue(getActiveFieldNames.imageName);
  const isTestEmailDisabled = !activeEmailBody || !activeEmailSubject;

  const testEmailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: testEmailValidation(),
    onSubmit: ({ email }) => {
      if (isTestEmailDisabled) {
        return;
      }

      dispatch(
        adminSendCampaignMail({
          model: imageTemplateConfig(formik.values),
          email: email.trim(),
          testImageUrl: activeEmailImage,
          testEmailBody: activeEmailBody,
          testEmailSubject: activeEmailSubject,
          testEmailCulture: language,
          userType,
          campaignId: '',
        }),
      );
    },
  });

  const onSendTestEmail = () => {
    if (isTestEmailDisabled) {
      return;
    }

    testEmailFormik.handleSubmit();
  };

  return {
    isSendDisabled,
    isTestEmailDisabled,
    onSendTestEmail,
    formik,
    testEmailFormik,
    getActiveFieldNames,
    language,
    setLanguage,
    onEditorReady,
  };
};

export default useConfigureCampaignEmail;
